import CardEngagementAccordion from "@/cards/common/component/deck-library/DeckCardExpand/CardEngagementAccordion";
import CardEngagementPanel from "@/cards/common/component/deck-library/DeckCardExpand/CardEngagementPanel";
import type { DeckCardThumbnailSummary } from "@/cards/common/types/app-deck/deckCardThumbnail.types";
import { parseSealDeadline } from "@/cards/common/utils/useSealTimeGate";
import {
  IMPACT_CURRENCY_LOCALE,
  formatImpactMoney,
} from "@/cards/impact-card/utils/impactCurrency";
import { DeckCardKakaoKind } from "@/modules/kakao/deckCard/deckCardKakaoKind";
import { useAuth } from "@/shared";
import { ROUTES } from "@/shared/constants";
import { useCurrentTalentProfile } from "@/shared/stores/useProfile";
import { HOPE_SANS, HOPE_SERIF } from "@/shared/branding/thehopeonebrand";
import {
  CARD_ASPECT_RATIO_CSS,
  CARD_BACK_BG,
  CARD_BORDER_RADIUS,
  cardBackScrollSx,
  scaledCardPx,
} from "@/shared/constants/cardLayout";
import { Share } from "@mui/icons-material";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import ShoppingBagOutlinedIcon from "@mui/icons-material/ShoppingBagOutlined";
import {
  Avatar,
  Box,
  Button,
  Stack,
  Typography,
} from "@mui/material";
import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import type { ImpactLocalPartner } from "../utils/impactCardData";
import { isInvitedImpactPartnerViewer } from "../utils/impactCardData";
import ImpactContributionDialog from "./ImpactContributionDialog";
import { useCollectImpactCard } from "../hooks/useCollectImpactCard";
import { useImpactCardContributions } from "../hooks/useImpactCardContributions";

/* -------------------------------------------------------------------------- */
/* Types                                                                      */
/* -------------------------------------------------------------------------- */

type ImpactContributionStatus =
  | "NONE"
  | "PENDING"
  | "COMMITTED"
  | "VERIFIED"
  | "FAILED"
  | "REFUNDED";

type ImpactContribution = {
  totalAmount?: number;
  verifiedAmount?: number;
  contributionCount?: number;
  status?: ImpactContributionStatus;
  latestContributionAt?: string | number | null;
};

type UseOfFundItem = {
  title: string;
  description?: string;
  estimatedAmount?: number;
};

type ImpactBeneficiary = {
  name?: string;
  type?: string;
  description?: string;
};

type ImpactUpdate = {
  id: number | string;
  title: string;
  description?: string;
  imageUrl?: string;
  createdAt?: string | number | null;
};

type ImpactCardFrontFaceProps = {
  cardId?: number;

  summary?: DeckCardThumbnailSummary | null;

  talentId?: number;
  talentName?: string;
  talentAvatarUrl?: string;

  cardTitle?: string;

  disableScroll?: boolean;

  isLoggedIn?: boolean;

  /**
   * Existing prop retained for backwards compatibility.
   *
   * For Impact Cards this now behaves as:
   * "Has this user collected the Card?"
   */
  isPurchased?: boolean;

  /**
   * Preferred Impact Card prop.
   *
   * If provided, this takes priority over isPurchased.
   */
  isCollected?: boolean;

  /** When this viewer collected the Card. */
  collectedAt?: string | number | null;

  /** When the Card was created / published. */
  createdAt?: string | number | null;

  isOwner?: boolean;

  onSignInClick?: () => void;

  /**
   * Existing callback retained for backwards compatibility.
   * It behaves as Collect Card when onCollectClick isn't supplied.
   */
  onPurchaseClick?: () => void;

  /**
   * Preferred Impact Card collection callback.
   */
  onCollectClick?: () => void;

  /**
   * Financial contribution is independent from collection.
   */
  onContributeClick?: () => void;

  onShare?: () => void;

  shareKind?: DeckCardKakaoKind;

  description?: string;

  shareable?: boolean;

  tableOfContents?: Array<Record<string, unknown>>;
  contentExps?: Array<Record<string, unknown>>;

  sealTime?: string | number | null;

  addBorder?: boolean;

  purchaseId?: number | string | null;

  onDownloadClick?: () => void;

  isDownloading?: boolean;

  /* ---------------------------------------------------------------------- */
  /* Impact Card fields                                                     */
  /* ---------------------------------------------------------------------- */

  causeCategory?: string;

  causeSummary?: string;

  location?: string;

  impactGoal?: string;

  expectedOutcome?: string;

  targetAmount?: number;

  committedFund?: number;

  verifiedFund?: number;

  progressPercentage?: number;

  contributorCount?: number;

  collectorCount?: number;

  clickedCount?: number;

  viewedCount?: number;

  useOfFunds?: UseOfFundItem[];

  beneficiary?: ImpactBeneficiary;

  localPartner?: ImpactLocalPartner;

  impactUpdates?: ImpactUpdate[];

  contribution?: ImpactContribution;

  /**
   * Card authenticity verification.
   *
   * This is intentionally separate from contribution verification.
   */
  isCardVerified?: boolean;
};

/* -------------------------------------------------------------------------- */
/* thehope.one Impact Card design system                                      */
/* -------------------------------------------------------------------------- */

const impact = {
  forestDeep: "#102C24",

  forest: "#1D4437",

  forestSoft: "#376B55",

  green: "#5E8F72",

  greenLight: "#8FB49B",

  greenSoft: "#DCEADF",

  greenMist: "#EEF5EF",

  ivory: "#FAF8F2",

  cream: "#F3F0E8",

  paper: "#FFFFFF",

  blue: "#7897A5",

  blueSoft: "#DDE9ED",

  sand: "#D7C6A5",

  sandSoft: "#EFE7D7",

  text: "#17231D",

  muted: "#68766F",

  mutedLight: "#89968F",

  border: "rgba(16,44,36,.12)",

  borderStrong: "rgba(16,44,36,.20)",

  verified: "#4F8767",

  warning: "#9A7A42",

  error: "#9A554F",
};

/* -------------------------------------------------------------------------- */
/* Shared styles                                                              */
/* -------------------------------------------------------------------------- */

const sectionLabelSx = {
  font: "var(--semibold-xxs)",

  letterSpacing: "0.12em",

  textTransform: "uppercase" as const,

  color: impact.green,

  pb: "var(--spacing-2x)",

  mb: 0.85,
};

const impactTileSx = {
  py: "var(--spacing-3x)",

  px: "var(--spacing-3x)",

  borderRadius: 2,

  bgcolor: impact.greenMist,

  border: `1px solid ${impact.border}`,
};

/* -------------------------------------------------------------------------- */
/* Component                                                                  */
/* -------------------------------------------------------------------------- */

const ImpactCardFrontFace: React.FC<ImpactCardFrontFaceProps> = ({
  cardId,

  summary,

  talentId,

  talentName,

  talentAvatarUrl,

  cardTitle,

  disableScroll = false,

  isLoggedIn = true,

  isPurchased = false,

  isCollected,

  collectedAt,

  createdAt,

  isOwner = false,

  onSignInClick,

  onPurchaseClick,

  onCollectClick,

  onContributeClick,

  onShare,

  shareKind = "impact",

  description,

  shareable = true,

  tableOfContents = [],

  contentExps = [],

  sealTime,

  addBorder = false,

  purchaseId,

  causeCategory,

  causeSummary,

  location,

  impactGoal: _impactGoal,

  expectedOutcome: _expectedOutcome,

  targetAmount = 0,

  committedFund = 0,

  verifiedFund = 0,

  progressPercentage,

  contributorCount = 0,

  collectorCount = 0,

  clickedCount = 0,

  viewedCount = 0,

  useOfFunds = [],

  beneficiary,

  localPartner,

  impactUpdates = [],

  contribution: contributionProp,

  isCardVerified = true,
}) => {
  const { t, i18n } = useTranslation("taascard");

  const { userInfo } = useAuth();
  const { currentProfile } = useCurrentTalentProfile();
  const isInvitedPartner = isInvitedImpactPartnerViewer(localPartner, [
    userInfo?.id,
    currentProfile?.id,
  ]);

  const navigate = useNavigate();

  const [contributionOpen, setContributionOpen] = useState(false);

  const live = useImpactCardContributions({
    mainCardId: cardId,
    cardTalentId: talentId,
    purchaseId,
  });
  const collection = useCollectImpactCard({ mainCardId: cardId });

  const displayVerifiedFund = live.fetched ? live.verifiedFund : verifiedFund;
  const displayCommittedFund = live.fetched ? live.committedFund : committedFund;
  const myContributions = useMemo(() => {
    if (!live.fetched || isOwner) return [];
    const withUser = live.contributors.filter((person) => person.userId != null);
    if (withUser.length > 0 && userInfo?.id) {
      return live.contributors.filter(
        (person) => String(person.userId) === String(userInfo.id)
      );
    }
    return live.contributors;
  }, [live.fetched, live.contributors, isOwner, userInfo?.id]);

  const myContributionTotal = myContributions.reduce(
    (sum, person) => sum + person.amount,
    0
  );

  const contribution =
    live.fetched
      ? isOwner
        ? {
            totalAmount: live.committedFund,
            verifiedAmount: live.verifiedFund,
            contributionCount: live.contributorCount,
            status: (live.contributorCount > 0
              ? "VERIFIED"
              : "NONE") as ImpactContributionStatus,
          }
        : {
            totalAmount: myContributionTotal,
            verifiedAmount: myContributionTotal,
            contributionCount: myContributions.length,
            status: (myContributionTotal > 0
              ? "VERIFIED"
              : "NONE") as ImpactContributionStatus,
          }
      : contributionProp;

  const scale = 1;

  /* ------------------------------------------------------------------------ */
  /* Backwards-compatible Impact Card state                                   */
  /* ------------------------------------------------------------------------ */

  const collected =
    collection.justCollected ||
    (typeof isCollected === "boolean"
      ? isCollected
      : isPurchased);

  const collectHandler =
    onCollectClick ?? onPurchaseClick;

  const canViewEngagement =
    isOwner || collected;

  const talentDisplayName =
    talentName ?? t("talent");

  /* ------------------------------------------------------------------------ */
  /* Progress                                                                 */
  /* ------------------------------------------------------------------------ */

  const calculatedProgress = useMemo(() => {
    if (live.fetched && targetAmount > 0) {
      return Math.min(
        100,
        Math.max(0, (displayVerifiedFund / targetAmount) * 100)
      );
    }

    if (
      typeof progressPercentage === "number" &&
      Number.isFinite(progressPercentage)
    ) {
      return Math.min(
        100,
        Math.max(0, progressPercentage)
      );
    }

    if (
      targetAmount <= 0 ||
      displayVerifiedFund <= 0
    ) {
      return 0;
    }

    return Math.min(
      100,
      Math.max(
        0,
        (displayVerifiedFund / targetAmount) * 100
      )
    );
  }, [
    live.fetched,
    progressPercentage,
    targetAmount,
    displayVerifiedFund,
  ]);

  /* ------------------------------------------------------------------------ */
  /* Currency                                                                 */
  /* ------------------------------------------------------------------------ */

  /** Impact funds are always presented in Korean won. */
  const formatMoney = (
    amount?: number
  ) =>
    formatImpactMoney(
      amount,
      i18n.resolvedLanguage ||
        i18n.language ||
        IMPACT_CURRENCY_LOCALE
    );

  /* ------------------------------------------------------------------------ */
  /* Date formatting                                                          */
  /* ------------------------------------------------------------------------ */

  const formatDate = (
    value?: string | number | null
  ) => {
    if (
      value === undefined ||
      value === null ||
      value === ""
    ) {
      return "";
    }

    const timestamp =
      parseSealDeadline(value) ??
      new Date(
        typeof value === "number"
          ? value
          : String(value)
      ).getTime();

    if (!Number.isFinite(timestamp)) {
      return "";
    }

    return new Intl.DateTimeFormat(
      i18n.resolvedLanguage ||
        i18n.language ||
        "en",
      {
        month: "short",

        day: "numeric",

        year: "numeric",
      }
    ).format(new Date(timestamp));
  };

  /* ------------------------------------------------------------------------ */
  /* Contribution                                                             */
  /* ------------------------------------------------------------------------ */

  const contributionStatus:
    ImpactContributionStatus =
    contribution?.status ?? "NONE";

  const hasContribution =
    contributionStatus !== "NONE" ||
    (contribution?.totalAmount ?? 0) > 0;

  const contributionStatusMeta =
    useMemo(() => {
      switch (contributionStatus) {
        case "VERIFIED":
          return {
            label: "Verified",
            icon: "✓",
            color: impact.verified,
            bg: "#E7F2EA",
          };

        case "COMMITTED":
          return {
            label: "Committed",
            icon: "●",
            color: impact.warning,
            bg: "#F3EEDF",
          };

        case "PENDING":
          return {
            label:
              "Verification pending",
            icon: "◷",
            color: "#6A7780",
            bg: impact.blueSoft,
          };

        case "FAILED":
          return {
            label:
              "Contribution failed",
            icon: "!",
            color: impact.error,
            bg: "#F3E5E3",
          };

        case "REFUNDED":
          return {
            label: "Refunded",
            icon: "↩",
            color: impact.muted,
            bg: impact.cream,
          };

        default:
          return {
            label:
              "No contribution yet",
            icon: "○",
            color: impact.muted,
            bg: impact.cream,
          };
      }
    }, [contributionStatus]);

  /* ------------------------------------------------------------------------ */
  /* Existing Talent Hall behavior                                            */
  /* ------------------------------------------------------------------------ */

  const handleGoToHall = (
    event: React.MouseEvent
  ) => {
    event.stopPropagation();

    if (!talentId) return;

    navigate(
      `${ROUTES.TALENT_HALL}/${talentId}`
    );
  };

  /* ------------------------------------------------------------------------ */
  /* Collection action                                                        */
  /* ------------------------------------------------------------------------ */

  const handleCollect = (
    event: React.MouseEvent
  ) => {
    event.stopPropagation();

    if (collection.isCollecting) return;

    if (!isLoggedIn) {
      onSignInClick?.();
      return;
    }

    if (!cardId) {
      collectHandler?.();
      return;
    }

    void collection.collect().catch(() => undefined);
  };

  /* ------------------------------------------------------------------------ */
  /* Contribution action                                                      */
  /* ------------------------------------------------------------------------ */

  const handleContribute = (
    event: React.MouseEvent
  ) => {
    event.stopPropagation();

    if (!isLoggedIn) {
      onSignInClick?.();
      return;
    }

    setContributionOpen(true);
  };

  /* ------------------------------------------------------------------------ */
  /* Sign-in gate                                                             */
  /* ------------------------------------------------------------------------ */

  const renderSignInRequired = () => (
    <Box
      sx={{
        p: "var(--spacing-4x)",

        borderRadius:
          "var(--spacing-3x)",

        bgcolor: impact.greenMist,

        border: `1px solid ${impact.border}`,

        textAlign: "center",
      }}
    >
      <Box
        sx={{
          width: 44,

          height: 44,

          borderRadius: "50%",

          mx: "auto",

          mb: "var(--spacing-2x)",

          display: "grid",

          placeItems: "center",

          bgcolor: impact.greenSoft,

          border: `1px solid ${impact.border}`,
        }}
      >
        <LockOutlinedIcon
          sx={{
            fontSize: 22,

            color: impact.forest,
          }}
        />
      </Box>

      <Typography
        sx={{
          font: "var(--semibold-sm)",

          color: impact.text,

          mb: "var(--spacing-1x)",
        }}
      >
        Sign in to collect this Impact
        Card
      </Typography>

      <Typography
        sx={{
          font: "var(--body-xxs)",

          color: impact.muted,

          lineHeight: 1.55,

          mb: onSignInClick
            ? "var(--spacing-3x)"
            : 0,
        }}
      >
        Collecting is free. You can
        support the cause now or
        contribute later whenever you
        are ready.
      </Typography>

      {onSignInClick ? (
        <Button
          size="small"
          onClick={(event) => {
            event.stopPropagation();

            onSignInClick();
          }}
          sx={{
            borderRadius: "999px",

            px: 2.5,

            textTransform: "none",

            fontWeight: 700,

            bgcolor:
              impact.forestDeep,

            color: "#FFFFFF",

            "&:hover": {
              bgcolor: impact.forest,
            },
          }}
        >
          Sign in
        </Button>
      ) : null}
    </Box>
  );

  /* ------------------------------------------------------------------------ */
  /* Collect section                                                          */
  /* ------------------------------------------------------------------------ */

  const renderCollectCard = () => (
    <Box
      sx={{
        p: "var(--spacing-4x)",

        borderRadius:
          "var(--spacing-3x)",

        bgcolor: impact.greenMist,

        border: `1px solid ${impact.border}`,

        textAlign: "center",
      }}
    >
      <Box
        sx={{
          width: 46,

          height: 46,

          borderRadius: "50%",

          mx: "auto",

          mb: "var(--spacing-2x)",

          display: "grid",

          placeItems: "center",

          bgcolor: impact.greenSoft,

          color: impact.forestDeep,

          fontSize: "1.35rem",
        }}
      >
        ♡
      </Box>

      <Typography
        sx={{
          fontFamily: HOPE_SERIF,
          fontSize: "1.18rem",
          fontWeight: 600,
          color: impact.forestDeep,
          mb: "var(--spacing-1x)",
        }}
      >
        Collect this Impact Card
      </Typography>

      <Typography
        sx={{
          fontFamily: HOPE_SANS,
          fontSize: "0.95rem",
          color: impact.muted,
          lineHeight: 1.65,
          maxWidth: 300,
          mx: "auto",
          mb: "var(--spacing-3x)",
        }}
      >
        Keep this cause in your
        collection and follow its
        progress. A donation is not
        required to collect the Card.
      </Typography>

      {cardId || collectHandler ? (
        <Button
          size="small"
          startIcon={
            <ShoppingBagOutlinedIcon
              sx={{
                fontSize:
                  "1rem !important",
              }}
            />
          }
          onClick={handleCollect}
          disabled={collection.isCollecting}
          sx={{
            borderRadius: "999px",

            px: 2.75,

            py: 0.8,

            textTransform: "none",

            fontWeight: 700,

            bgcolor:
              impact.forestDeep,

            color: "#FFFFFF",

            "&:hover": {
              bgcolor: impact.forest,
            },
          }}
        >
          {collection.isCollecting
            ? "Collecting…"
            : "Collect Impact Card"}
        </Button>
      ) : null}

      <Typography
        sx={{
          mt: 1.5,

          font: "var(--body-xxs)",

          color: impact.mutedLight,
        }}
      >
        You can contribute later.
      </Typography>
    </Box>
  );

  /* ------------------------------------------------------------------------ */
  /* User Impact status                                                       */
  /* ------------------------------------------------------------------------ */

  const renderUserImpactStatus =
    () => (
      <Box>
        <Typography
          sx={sectionLabelSx}
        >
          Your Impact
        </Typography>

        <Stack
          spacing="var(--spacing-2x)"
        >
          {/* Collected */}

          <Box sx={impactTileSx}>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              spacing={2}
            >
              <Box>
                <Typography
                  sx={{
                    font:
                      "var(--semibold-xs)",

                    color: impact.text,
                  }}
                >
                  Impact Card
                </Typography>

                <Typography
                  sx={{
                    mt: 0.4,

                    font:
                      "var(--body-xxs)",

                    color:
                      impact.muted,
                  }}
                >
                  This Card is part of
                  your collection.
                </Typography>
              </Box>

              <Box
                sx={{
                  px: 1.2,

                  py: 0.5,

                  borderRadius:
                    "999px",

                  bgcolor:
                    "#E3F0E6",

                  color:
                    impact.verified,

                  font:
                    "var(--semibold-xxs)",

                  flexShrink: 0,
                }}
              >
                ✓ Collected
              </Box>
            </Stack>
          </Box>

          {/* Contribution */}

          <Box sx={impactTileSx}>
            <Stack
              direction="row"
              alignItems="flex-start"
              justifyContent="space-between"
              spacing={2}
            >
              <Box
                sx={{
                  minWidth: 0,
                }}
              >
                <Typography
                  sx={{
                    font:
                      "var(--semibold-xs)",

                    color: impact.text,
                  }}
                >
                    {isOwner ? "Contributors" : "Your Contribution"}
                </Typography>

                {hasContribution ? (
                  <>
                    <Typography
                      sx={{
                        mt: 0.6,

                        font:
                          "var(--semibold-sm)",

                        color:
                          impact.forestDeep,
                      }}
                    >
                      {formatMoney(
                        contribution
                          ?.totalAmount ??
                          0
                      )}
                    </Typography>

                    {(contribution
                      ?.contributionCount ??
                      0) > 1 ? (
                      <Typography
                        sx={{
                          mt: 0.25,

                          font:
                            "var(--body-xxs)",

                          color:
                            impact.muted,
                        }}
                      >
                        {
                          contribution
                            ?.contributionCount
                        }{" "}
                        contributions
                      </Typography>
                    ) : null}

                    {contribution
                      ?.latestContributionAt ? (
                      <Typography
                        sx={{
                          mt: 0.25,

                          font:
                            "var(--body-xxs)",

                          color:
                            impact.muted,
                        }}
                      >
                        Latest{" "}
                        {formatDate(
                          contribution.latestContributionAt
                        )}
                      </Typography>
                    ) : null}
                  </>
                ) : (
                  <Typography
                    sx={{
                      mt: 0.6,

                      font:
                        "var(--body-xs)",

                      color:
                        impact.muted,
                    }}
                  >
                    {isOwner
                      ? "No contributions yet."
                      : "You have not contributed yet."}
                  </Typography>
                )}
              </Box>

              <Box
                sx={{
                  px: 1.2,

                  py: 0.5,

                  borderRadius:
                    "999px",

                  bgcolor:
                    contributionStatusMeta.bg,

                  color:
                    contributionStatusMeta.color,

                  font:
                    "var(--semibold-xxs)",

                  flexShrink: 0,

                  whiteSpace: "nowrap",
                }}
              >
                {
                  contributionStatusMeta.icon
                }{" "}
                {
                  contributionStatusMeta.label
                }
              </Box>
            </Stack>

            {!isOwner && !isInvitedPartner && collected ? (
              <Button
                fullWidth
                onClick={
                  handleContribute
                }
                sx={{
                  mt: 2,

                  borderRadius:
                    "999px",

                  py: 1,

                  textTransform: "none",

                  font:
                    "var(--semibold-xs)",

                  bgcolor:
                    impact.green,

                  color: "#FFFFFF",

                  "&:hover": {
                    bgcolor:
                      impact.forestSoft,
                  },
                }}
              >
                {hasContribution
                  ? "Contribute again"
                  : "Contribute"}
              </Button>
            ) : null}

            {isOwner && live.fetched ? (
              <Stack spacing={1} sx={{ mt: 1.5 }}>
                {live.contributors.slice(0, 8).map((person) => (
                  <Stack
                    key={String(person.id)}
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    spacing={1}
                  >
                    <Typography
                      sx={{
                        font: "var(--semibold-xxs)",
                        color: impact.text,
                        minWidth: 0,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {person.name}
                    </Typography>
                    <Typography
                      sx={{
                        font: "var(--semibold-xxs)",
                        color: impact.forestDeep,
                        flexShrink: 0,
                      }}
                    >
                      {formatMoney(person.amount)}
                    </Typography>
                  </Stack>
                ))}
              </Stack>
            ) : null}
          </Box>
        </Stack>
      </Box>
    );

  /* ------------------------------------------------------------------------ */
  /* Go to Talent Hall                                                        */
  /* ------------------------------------------------------------------------ */

  const renderGoToHallButton =
    () =>
      talentId ? (
        <Button
          fullWidth
          variant="outlined"
          endIcon={
            <ArrowForwardRoundedIcon
              sx={{
                fontSize:
                  "1rem !important",
              }}
            />
          }
          onClick={handleGoToHall}
          sx={{
            mt: "var(--spacing-2x)",

            borderRadius: 1.5,

            py: "var(--spacing-2x)",

            textTransform: "none",

            fontFamily: HOPE_SANS,
            fontSize: "0.95rem",
            fontWeight: 700,

            bgcolor: impact.cream,

            color: impact.text,

            borderColor:
              impact.borderStrong,

            "&:hover": {
              borderColor:
                impact.green,

              bgcolor:
                impact.greenMist,
            },
          }}
        >
          {t(
            "deck_summary_go_to_talents_hall"
          )}
        </Button>
      ) : null;

  /* ------------------------------------------------------------------------ */
  /* Back face                                                                */
  /* ------------------------------------------------------------------------ */

  const Backface = () => {
    return (
      <Box
        sx={{
          width: "100%",

          height: "100%",

          aspectRatio:
            CARD_ASPECT_RATIO_CSS,

          borderRadius:
            scaledCardPx(
              CARD_BORDER_RADIUS,
              scale
            ),

          overflow: "hidden",

          bgcolor: impact.ivory,

          border: `1px solid ${impact.borderStrong}`,

          display: "flex",

          flexDirection: "column",

          color: impact.text,

          ...(!disableScroll
            ? {}
            : {
                touchAction: "none",
              }),
        }}
      >
        {/* ------------------------------------------------------------------ */}
        {/* subtle thehope.one identity line                                   */}
        {/* ------------------------------------------------------------------ */}

        <Box
          sx={{
            height: 3,

            flexShrink: 0,

            background: `linear-gradient(
              90deg,
              ${impact.forestDeep},
              ${impact.green},
              ${impact.greenLight},
              ${impact.blue}
            )`,
          }}
        />

        <Box
          sx={{
            ...cardBackScrollSx,

            flex: 1,

            minHeight: 0,

            overflowY: "auto",

            overflowX: "hidden",

            touchAction: "pan-y",

            overscrollBehavior:
              "contain",

            WebkitOverflowScrolling:
              "touch",

            px: "var(--spacing-4x)",

            pt: "var(--spacing-4x)",

            pb: "var(--spacing-3x)",

            background: `
              radial-gradient(
                circle at 90% 3%,
                rgba(94,143,114,.10),
                transparent 24%
              ),
              ${impact.ivory}
            `,
          }}
        >
          <Stack spacing={2.35}>
            <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1.5}>
              <Box>
                <Typography
                  sx={{
                    fontFamily: HOPE_SANS,
                    fontSize: "0.72rem",
                    fontWeight: 700,
                    letterSpacing: "0.16em",
                    textTransform: "uppercase",
                    color: impact.green,
                  }}
                >
                  thehope.one
                </Typography>
                <Typography
                  sx={{
                    mt: 0.35,
                    fontFamily: HOPE_SANS,
                    fontSize: "0.92rem",
                    fontWeight: 800,
                    letterSpacing: "0.08em",
                    color: impact.forestDeep,
                  }}
                >
                  IMPACT CARD
                </Typography>
              </Box>

              {shareable && onShare && isLoggedIn ? (
                <Button
                  size="small"
                  startIcon={<Share sx={{ fontSize: "1.05rem !important" }} />}
                  onClick={(event) => {
                    event.stopPropagation();
                    onShare();
                  }}
                  sx={{
                    flexShrink: 0,
                    borderRadius: "999px",
                    px: 1.6,
                    py: 0.55,
                    textTransform: "none",
                    fontFamily: HOPE_SANS,
                    fontSize: "0.82rem",
                    fontWeight: 700,
                    color: impact.forest,
                    border: `1px solid ${impact.borderStrong}`,
                    "&:hover": { bgcolor: impact.greenMist, borderColor: impact.green },
                  }}
                >
                  {t("share_card_action")}
                </Button>
              ) : null}
            </Stack>

            {talentName ? (
              <Stack direction="row" alignItems="center" spacing={1.35}>
                <Avatar
                  src={talentAvatarUrl || undefined}
                  alt={talentName}
                  sx={{
                    width: 52,
                    height: 52,
                    border: `1px solid ${impact.borderStrong}`,
                    bgcolor: impact.greenSoft,
                    color: impact.forestDeep,
                    fontFamily: HOPE_SERIF,
                    fontSize: "1.2rem",
                  }}
                >
                  {talentName.charAt(0).toUpperCase()}
                </Avatar>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography
                    sx={{
                      fontFamily: HOPE_SANS,
                      fontSize: "0.72rem",
                      fontWeight: 700,
                      letterSpacing: "0.12em",
                      textTransform: "uppercase",
                      color: impact.muted,
                    }}
                  >
                    Created by
                  </Typography>
                  <Typography
                    sx={{
                      mt: 0.2,
                      fontFamily: HOPE_SERIF,
                      fontSize: "1.18rem",
                      fontWeight: 600,
                      color: impact.forestDeep,
                      lineHeight: 1.2,
                    }}
                  >
                    {talentName}
                  </Typography>
                </Box>
                {isCardVerified ? (
                  <Box
                    sx={{
                      px: 1.35,
                      py: 0.55,
                      borderRadius: "999px",
                      bgcolor: "#E4F0E7",
                      color: impact.verified,
                      fontFamily: HOPE_SANS,
                      fontSize: "0.78rem",
                      fontWeight: 700,
                      flexShrink: 0,
                    }}
                  >
                    ✓ Verified
                  </Box>
                ) : null}
              </Stack>
            ) : null}

            <Box>
              {causeCategory ? (
                <Typography
                  sx={{
                    fontFamily: HOPE_SANS,
                    fontSize: "0.78rem",
                    fontWeight: 700,
                    color: impact.green,
                    textTransform: "uppercase",
                    letterSpacing: "0.12em",
                  }}
                >
                  {causeCategory}
                </Typography>
              ) : null}
              <Typography
                sx={{
                  mt: causeCategory ? 0.7 : 0,
                  fontFamily: HOPE_SERIF,
                  fontWeight: 700,
                  fontSize: { xs: "1.55rem", sm: "1.72rem" },
                  lineHeight: 1.18,
                  letterSpacing: "-0.02em",
                  color: impact.forestDeep,
                }}
              >
                {cardTitle || "Impact Card"}
              </Typography>
              {location ? (
                <Typography
                  sx={{
                    mt: 0.7,
                    fontFamily: HOPE_SANS,
                    fontSize: "0.92rem",
                    color: impact.muted,
                  }}
                >
                  {location}
                </Typography>
              ) : null}
            </Box>

            {causeSummary || description ? (
              <Box
                sx={{
                  pl: 1.85,
                  borderLeft: `3px solid ${impact.green}`,
                }}
              >
                <Typography
                  sx={{
                    fontFamily: HOPE_SANS,
                    fontSize: "0.7rem",
                    fontWeight: 700,
                    letterSpacing: "0.14em",
                    textTransform: "uppercase",
                    color: impact.green,
                    mb: 0.85,
                  }}
                >
                  About this cause
                </Typography>
                {/* The back face stays a summary: the full story lives in the book pages. */}
                <Typography
                  sx={{
                    fontFamily: HOPE_SERIF,
                    fontStyle: "italic",
                    fontSize: { xs: "1.08rem", sm: "1.16rem" },
                    lineHeight: 1.72,
                    color: impact.forestDeep,
                    display: "-webkit-box",
                    WebkitBoxOrient: "vertical",
                    WebkitLineClamp: 3,
                    overflow: "hidden",
                  }}
                >
                  {causeSummary || description}
                </Typography>
              </Box>
            ) : null}

            <Box>
              <Stack
                direction="row"
                spacing={1}
                flexWrap="wrap"
                useFlexGap
              >
                {[
                  formatDate(createdAt)
                    ? `Created ${formatDate(createdAt)}`
                    : null,
                  collected && formatDate(collectedAt)
                    ? `Collected ${formatDate(collectedAt)}`
                    : null,
                  isCardVerified ? "✓ Verified" : null,
                ]
                  .filter(Boolean)
                  .map((label) => (
                    <Box
                      key={label as string}
                      sx={{
                        px: 1.1,
                        py: 0.4,
                        borderRadius: "999px",
                        bgcolor: impact.cream,
                        border: `1px solid ${impact.border}`,
                        fontFamily: HOPE_SANS,
                        fontSize: "0.72rem",
                        fontWeight: 700,
                        color: impact.muted,
                      }}
                    >
                      {label}
                    </Box>
                  ))}
              </Stack>
            </Box>

            {!isLoggedIn
              ? renderSignInRequired()
              : isOwner || isInvitedPartner
                ? null
                : !collected
                  ? renderCollectCard()
                  : null}

            {renderGoToHallButton()}
          </Stack>
        </Box>
      </Box>
    );
  };

  /* ------------------------------------------------------------------------ */
  /* Existing border behavior                                                 */
  /* ------------------------------------------------------------------------ */

  return (
    <>
      {addBorder ? (
        <Box
          sx={{
            borderRadius: scaledCardPx(CARD_BORDER_RADIUS, scale),
          }}
        >
          <Backface />
        </Box>
      ) : (
        <Backface />
      )}

      <ImpactContributionDialog
        open={contributionOpen}
        onClose={() => setContributionOpen(false)}
        title={cardTitle ?? "this cause"}
        targetAmount={targetAmount}
        firstFundLine={useOfFunds[0]?.title}
        beneficiaryName={beneficiary?.name}
        onSubmit={cardId ? live.contribute : undefined}
        submitting={live.isSubmitting}
      />
    </>
  );
};

export default ImpactCardFrontFace;